import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import DateRangePicker from 'rsuite/DateRangePicker';
import { startOfDay, endOfDay, subMonths, subYears, subWeeks } from 'date-fns';
import 'rsuite/DateRangePicker/styles/index.css';

const UIDateRangePicker: React.FC<any> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentYear = new Date().getFullYear();

  const clampToCurrentYear = (date: Date): Date => {
    if (date.getFullYear() > currentYear) {
      return new Date(currentYear, date.getMonth(), date.getDate());
    }
    return date;
  };

  const handleChange = useCallback((value: [Date, Date] | null) => {
    if (value && props.onChange) {
      const [start, end] = value;
      props.onChange([clampToCurrentYear(start), clampToCurrentYear(end)]);
    } else if (props.onChange) {
      props.onChange(value);
    }
  }, [props.onChange, currentYear]);

  const ranges = useMemo(() => [
    {
      label: 'Last Week',
      value: [startOfDay(subWeeks(new Date(), 1)), endOfDay(new Date())]
    },
    {
      label: 'Last Month',
      value: [startOfDay(subMonths(new Date(), 1)), endOfDay(new Date())]
    },
    {
      label: 'Last 3 Months',
      value: [startOfDay(subMonths(new Date(), 3)), endOfDay(new Date())]
    },
    {
      label: 'Last 6 Months',
      value: [startOfDay(subMonths(new Date(), 6)), endOfDay(new Date())]
    },
    {
      label: 'Last 1 Year',
      value: [startOfDay(subYears(new Date(), 1)), endOfDay(new Date())]
    },
    {
      label: 'Last 5 Years',
      value: [startOfDay(subYears(new Date(), 5)), endOfDay(new Date())]
    }
  ], []);

  // Close picker when window is resized (screen size changes)
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    if (props.disabled) return;

    // Only toggle if clicking on the input/trigger area, not the popup
    const target = e.target as HTMLElement;
    const isPickerPopup = target.closest('.rs-picker-popup');
    
    if (!isPickerPopup) {
      setIsOpen(prev => !prev);
    }
  }, [props.disabled]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <div ref={containerRef} onClick={handleContainerClick} style={{ display: 'contents' }}>
      <DateRangePicker 
        {...props} 
        ranges={ranges} 
        open={isOpen}
        onClose={handleClose}
        onChange={handleChange}
      />
    </div>
  );
};

export default React.memo(UIDateRangePicker);
